import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn.js";
const TooltipContext = React.createContext(null);
function useTooltipContext(part) {
    const ctx = React.useContext(TooltipContext);
    if (!ctx)
        throw new Error(`${part} must be used inside <Tooltip>`);
    return ctx;
}
/**
 * A small, styled hover/focus hint (no entrance animation: it just appears in place) — the replacement for the browser's plain `title` tooltip.
 * Compose it like a shadcn tooltip:
 *
 *   <Tooltip>
 *     <TooltipTrigger render={<Button variant="secondary">Hover</Button>} />
 *     <TooltipContent>Add to library</TooltipContent>
 *   </Tooltip>
 *
 * Shows on hover and on keyboard focus, hides on leave, blur, Esc, scroll or click, and is
 * wired to its trigger with `aria-describedby` so screen readers announce it. Never put
 * anything essential in one — a tooltip is a hint, and touch screens don't hover.
 */
export function Tooltip({ children, delayMs = 150 }) {
    const [open, setOpen] = React.useState(false);
    const triggerRef = React.useRef(null);
    const timer = React.useRef(null);
    const contentId = React.useId();
    const clearTimer = React.useCallback(() => {
        if (timer.current)
            clearTimeout(timer.current);
        timer.current = null;
    }, []);
    const show = React.useCallback(() => {
        clearTimer();
        if (delayMs <= 0)
            setOpen(true);
        else
            timer.current = setTimeout(() => setOpen(true), delayMs);
    }, [clearTimer, delayMs]);
    const hide = React.useCallback(() => {
        clearTimer();
        setOpen(false);
    }, [clearTimer]);
    React.useEffect(() => clearTimer, [clearTimer]);
    React.useEffect(() => {
        if (!open)
            return;
        const onKey = (event) => {
            if (event.key === "Escape")
                hide();
        };
        window.addEventListener("keydown", onKey);
        window.addEventListener("scroll", hide, true);
        window.addEventListener("resize", hide);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("scroll", hide, true);
            window.removeEventListener("resize", hide);
        };
    }, [open, hide]);
    const value = React.useMemo(() => ({ open, contentId, triggerRef, show, hide }), [open, contentId, show, hide]);
    return _jsx(TooltipContext.Provider, { value: value, children: children });
}
function chain(own, ours) {
    return (event) => {
        own?.(event);
        ours();
    };
}
export function TooltipTrigger({ render, children }) {
    const ctx = useTooltipContext("TooltipTrigger");
    const setRef = (node) => {
        ctx.triggerRef.current = node;
    };
    if (!render) {
        return (_jsx("span", { ref: setRef, className: "inline-flex", "aria-describedby": ctx.open ? ctx.contentId : undefined, onMouseEnter: ctx.show, onMouseLeave: ctx.hide, onFocus: ctx.show, onBlur: ctx.hide, children: children }));
    }
    const own = render.props;
    const ownRef = render.ref;
    return React.cloneElement(render, {
        ref: (node) => {
            setRef(node);
            if (typeof ownRef === "function")
                ownRef(node);
            else if (ownRef && typeof ownRef === "object") {
                ownRef.current = node;
            }
        },
        "aria-describedby": ctx.open ? ctx.contentId : own["aria-describedby"],
        onMouseEnter: chain(own.onMouseEnter, ctx.show),
        onMouseLeave: chain(own.onMouseLeave, ctx.hide),
        onFocus: chain(own.onFocus, ctx.show),
        onBlur: chain(own.onBlur, ctx.hide),
        // A click means the person is acting, not reading a hint.
        onClick: chain(own.onClick, ctx.hide),
    });
}
const VIEWPORT_MARGIN = 8;
/** Keep the pointer this far from the box's rounded corners. */
const ARROW_INSET = 10;
export function TooltipContent({ children, side = "top", sideOffset = 8, arrow = true, className, }) {
    const ctx = useTooltipContext("TooltipContent");
    const contentRef = React.useRef(null);
    const [position, setPosition] = React.useState(null);
    React.useLayoutEffect(() => {
        if (!ctx.open) {
            setPosition(null);
            return;
        }
        const trigger = ctx.triggerRef.current;
        const content = contentRef.current;
        if (!trigger || !content)
            return;
        const anchor = trigger.getBoundingClientRect();
        const { width, height } = content.getBoundingClientRect();
        const above = anchor.top - height - sideOffset;
        const below = anchor.bottom + sideOffset;
        const fitsAbove = above >= VIEWPORT_MARGIN;
        const fitsBelow = below + height <= window.innerHeight - VIEWPORT_MARGIN;
        const placeAbove = side === "top" ? fitsAbove || !fitsBelow : !fitsBelow && fitsAbove;
        const centred = anchor.left + anchor.width / 2 - width / 2;
        const left = Math.min(Math.max(centred, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN));
        const arrowCentre = anchor.left + anchor.width / 2 - left;
        const arrowLeft = Math.min(Math.max(arrowCentre, ARROW_INSET), width - ARROW_INSET);
        setPosition({ left, top: placeAbove ? above : below, arrowLeft, above: placeAbove });
    }, [ctx.open, ctx.triggerRef, side, sideOffset, children]);
    if (!ctx.open)
        return null;
    return createPortal(_jsxs("div", { ref: contentRef, id: ctx.contentId, role: "tooltip", style: {
            position: "fixed",
            left: position?.left ?? 0,
            top: position?.top ?? 0,
            // Measured first, shown once placed — no flash in the wrong spot.
            visibility: position ? "visible" : "hidden",
        }, className: cn("pointer-events-none z-[1000] max-w-xs rounded-md bg-fg-primary px-2.5 py-1.5 text-caption text-surface shadow-md", className), children: [children, arrow && position ? (_jsx("span", { "aria-hidden": "true", style: { left: position.arrowLeft - 4 }, className: cn("absolute size-2 rotate-45 bg-fg-primary", position.above ? "-bottom-1" : "-top-1") })) : null] }), document.body);
}
//# sourceMappingURL=tooltip.js.map